// Lightweight i18n (PLAN-P5 U2; B5B-I18N-WCAG). NL is the default (Dutch market); EN available.
// No external dep — a message catalog + context + t() hook. Language initialises from the booted
// session (tenant defaultLanguage) and a localStorage override, and is reflected on <html lang>.
// String coverage is iterative: nav + dashboard + common chrome first, expand per screen.
import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";

export type Lang = "nl" | "en";

type Catalog = Record<string, string>;
const NL: Catalog = {
  "nav.dashboard": "Dashboard",
  "persona.inspecteur": "Inspecteur", "persona.beleidsmaker": "Beleidsmaker", "persona.planner": "Planner",
  "persona.controller": "Controller", "persona.tickets": "Tickets", "persona.rapporten": "Rapporten",
  "ctx.organisation": "Organisatie", "ctx.regulation": "Verordening",
  "nav.account": "Account", "nav.logout": "Uitloggen", "nav.overview": "Overzicht", "nav.accountability": "Verantwoording",
  "nav.reports": "Rapporten", "nav.mission": "Missie", "nav.conduct": "Gedragsregels", "nav.goalPlanning": "Hoofddoelplanning",
  "nav.regime": "Regime configureren", "nav.tasks": "Taken", "nav.filingSystems": "Bestandssystemen", "nav.regulations": "Wet- en regelgeving",
  "nav.verwerkingen": "Verwerkingen",
  "nav.activities": "Activiteiten",
  "nav.assets": "Bedrijfsmiddelen",
  "nav.threats": "Bedreigingen",
  "nav.risks": "Risico's",
  "nav.dpia": "DPIA's",
  "nav.evidence": "Bewijs",
  "ev.files": "Bestanden",
  "ev.upload": "Uploaden",
  "ev.uploading": "Uploaden…",
  "ev.download": "Downloaden",
  "ev.none": "Nog geen bestanden.",
  "ev.maxSize": "max. 25 MB",
  "nav.tickets": "Tickets",
  "nav.dsar": "DSAR",
  "nav.transfers": "Doorgiften",
  "nav.actielijst": "Actielijst",
  "nav.programs": "Programma's",
  "nav.pdca": "PDCA",
  "nav.improvements": "Verbeterpunten",
  "nav.sprintGates": "Sprint-gates",
  "nav.changeFeed": "Wijzigingen",
  "nav.switchTenant": "Wissel van tenant",
  "nav.billing": "Facturering",
  "nav.trustExchange": "Trust Exchange",
  "nav.consultants": "Consultants",
  "nav.auditors": "Auditors",
  "dash.title": "Compliance-cockpit",
  "dash.tenant": "tenant",
  "dash.verwerkingen.title": "Verwerkingen",
  "dash.verwerkingen.body": "Uw AVG art. 30-register van verwerkingsactiviteiten.",
  "dash.tickets.title": "Tickets",
  "dash.tickets.body": "Openstaande werkitems en compliance-taken.",
  "dash.deadlines.title": "Aankomende deadlines",
  "dash.deadlines.none": "Geen aankomende deadlines.",
  "dash.deadlines.overdue": "te laat",
  "notif.aria": "Meldingen",
  "notif.title": "Meldingen",
  "notif.empty": "Geen meldingen.",
  "nav.partners": "Partners",
  "partners.title": "Partners & verwerkers",
  "partners.sub": "Art. 28 verwerkers + cross-tenant engagements",
  "partners.engagements": "Gedeelde engagements",
  "partners.engagements.intro": "Tweezijdige verwerker-engagements; status wordt 'active' zodra beide partijen bevestigen.",
  "partners.engagements.none": "Nog geen engagements.",
  "partners.newEngagement": "+ Nieuw engagement",
  "partners.engagementName": "Naam van het engagement",
  "partners.processorTenantId": "Verwerker tenant-ID (optioneel)",
  "partners.name": "Naam",
  "partners.role": "Rol",
  "partners.controller": "verwerkingsverantwoordelijke",
  "partners.processor": "verwerker",
  "partners.confirm": "Bevestigen",
  "partners.dpas": "Gedeelde verwerkersovereenkomsten",
  "partners.newDpa": "+ Nieuwe DPA",
  "partners.engagement": "Engagement",
  "partners.dpaVersion": "DPA-versie",
  "partners.dpas.needEngagement": "Maak eerst een engagement aan.",
  "partners.family": "Art. 28 partnerfamilie",
  "partners.family.gated": "Aanmaken van partnerrollen vereist de partnerdirectory (nog niet beschikbaar). Hieronder een alleen-lezen overzicht.",
  "partners.roles": "Partnerrollen",
  "partners.agreements": "Verwerkersovereenkomsten",
  "partners.schedules": "Verwerkingsschema's",
  "partners.subProcessors": "Subverwerkers",
  "partners.overview.none": "Geen items.",
  "ins.partners.title": "Partners",
  "ins.partners.sub": "Eén partnerregister, getoond per rol (verwerkingsverantwoordelijken, verwerkers, ontvangers, joint controllers, contactpersonen).",
  "ins.partners.addRole": "Rol toevoegen",
  "ins.partners.noParties": "Nog geen externe partijen. Voeg er eerst één toe via 'Externe partijen' in het Inspecteur-menu.",
  "nav.externalParties": "Externe partijen",
  "ins.parties.title": "Externe partijen",
  "ins.parties.empty": "Nog geen externe partijen. Voeg de organisaties toe waarmee u gegevens deelt (verwerkers, ontvangers, gezamenlijke verwerkingsverantwoordelijken, contactpersonen).",
  "ins.parties.field.name": "Naam",
  "ins.parties.field.legalName": "Juridische naam",
  "ins.parties.field.kind": "Soort partij",
  "ins.parties.field.country": "Land",
  "ins.parties.field.contactName": "Contactpersoon",
  "ins.parties.field.contactEmail": "Contact e-mail",
  "ins.parties.field.notes": "Notities",
  "ins.partners.tab.controllers": "Verwerkingsverantwoordelijken",
  "ins.partners.tab.processors": "Verwerkers",
  "ins.partners.tab.recipients": "Ontvangers",
  "ins.partners.tab.jointControllers": "Joint controllers",
  "ins.partners.tab.contacts": "Contactpersonen",
  "ins.partners.col.partner": "Partner",
  "ins.partners.col.role": "Rol",
  "ins.partners.empty.controllers": "Nog geen verwerkingsverantwoordelijken.",
  "ins.partners.empty.processors": "Nog geen verwerkers.",
  "ins.partners.empty.recipients": "Nog geen ontvangers.",
  "ins.partners.empty.jointControllers": "Nog geen joint controllers.",
  "ins.partners.empty.contacts": "Nog geen contactpersonen.",
  "ins.partners.failed": "Laden van partners mislukt:",
  "ins.partners.role.controller": "Verwerkingsverantwoordelijke",
  "ins.partners.role.data_processor": "Verwerker",
  "ins.partners.role.data_recipient": "Ontvanger",
  "ins.partners.role.joint_controller": "Joint controller",
  "ins.partners.role.sub_processor": "Subverwerker",
  "ins.partners.role.consultancy_partner": "Adviespartner",
  "ins.partners.role.auditor": "Auditor",
  "ins.partners.role.representative_art27": "Vertegenwoordiger (art. 27)",
  "ins.partners.role.dpo_art37": "Functionaris gegevensbescherming (art. 37)",
  "nav.team": "Team",
  "nav.orgDetails": "Organisatiegegevens",
  "nav.jurisdictietaal": "Jurisdictietaal instellingen",
  "jt.title": "Jurisdictietaal instellingen", "jt.sub": "Geef aan in welke jurisdicties uw organisatie actief is en welke taal per jurisdictie wordt gebruikt.",
  "jt.primary": "Primair", "jt.jurisdiction": "Jurisdictie", "jt.language": "Taal", "jt.add": "Jurisdictie toevoegen",
  "jt.save": "Opslaan", "jt.saved": "Jurisdictietaal-instellingen opgeslagen.", "jt.none": "Geen jurisdicties beschikbaar.",
  "nav.profile": "Mijn profiel",
  // Organisatiegegevens (Organisatie; W29)
  "org.title": "Organisatiegegevens",
  "org.sub": "De gegevens van uw organisatie. Juridische naam, KvK- en btw-nummer worden door de beheerder vastgelegd; u beheert de handelsnaam en contactgegevens.",
  "org.legalName": "Juridische naam",
  "org.tradeName": "Handelsnaam",
  "org.registration": "KvK-nummer",
  "org.vat": "Btw-nummer",
  "org.readonlyNote": "Juridische naam, KvK- en btw-nummer zijn alleen-lezen (beheerd door de super-tenant).",
  "org.contact": "Contactgegevens",
  "org.contact.address": "Adres",
  "org.contact.postalCode": "Postcode",
  "org.contact.city": "Plaats",
  "org.contact.country": "Land",
  "org.contact.email": "E-mail",
  "org.contact.phone": "Telefoon",
  "org.saved": "Organisatiegegevens opgeslagen.",
  "nav.vestigingen": "Vestigingen",
  "nav.privacyteam": "Privacyteam",
  "crud.remove": "Verwijderen",
  // Vestigingen (Organisatie; W29)
  "vest.title": "Vestigingen",
  "vest.sub": "De vestigingen (locaties) van uw organisatie.",
  "vest.new": "Nieuwe vestiging",
  "vest.empty": "Nog geen vestigingen.",
  "vest.name": "Naam",
  "vest.address": "Adres",
  "vest.postalCode": "Postcode",
  "vest.city": "Plaats",
  "vest.country": "Land",
  // Privacyteam (Organisatie; W29)
  "pt.title": "Privacyteam",
  "pt.sub": "De leden van het privacyteam en hun functie binnen de organisatie.",
  "pt.member": "Lid",
  "pt.email": "E-mail",
  "pt.functie": "Functie",
  "pt.add": "Lid toevoegen",
  "pt.pickUser": "Kies een gebruiker…",
  "pt.pickFunctie": "Kies een functie…",
  "pt.empty": "Nog geen teamleden.",
  "pt.noFuncties": "Er zijn nog geen functies beschikbaar. De Functie-bibliotheek wordt door de super-tenant (eigenaar/juridisch) beheerd; zodra functies zijn toegevoegd, kunt u teamleden een functie toewijzen.",
  // Planner · Taken (tasks; W30)
  "taken.title": "Taken",
  "taken.sub": "Taken voor het privacyteam, eventueel gekoppeld aan een ingeschakelde activiteit.",
  "taken.new": "Nieuwe taak",
  "taken.empty": "Nog geen taken.",
  "taken.taskTitle": "Titel",
  "taken.description": "Omschrijving",
  "taken.activity": "Activiteit",
  "taken.assignee": "Toegewezen aan",
  "taken.due": "Deadline",
  "taken.status": "Status",
  "taken.pickActivity": "Kies een activiteit…",
  "taken.pickAssignee": "Kies een gebruiker…",
  "taken.st.open": "Open",
  "taken.st.in_progress": "In behandeling",
  "taken.st.done": "Afgerond",
  "taken.st.cancelled": "Geannuleerd",
  // Organisatie · Ticket instellen (W30)
  "nav.ticketInstellen": "Ticket instellen",
  "nav.implPartner": "Implementatiepartner",
  // Implementatiepartner onboarding (W37)
  "impl.title": "Implementatiepartner",
  "impl.sub": "Schakel een advieskantoor in als implementatiepartner en nodig hun consultants uit. Uitgenodigde consultants krijgen toegang tot het partnercockpit voor uw organisatie.",
  "impl.signin": "Log in als beheerder om implementatiepartners te beheren.",
  "impl.engage": "Advieskantoor inschakelen",
  "impl.engage.sub": "Kies een kantoor uit de directory en schakel het in als partner.",
  "impl.engage.btn": "Inschakelen",
  "impl.engaging": "Bezig…",
  "impl.firm": "Advieskantoor",
  "impl.firm.pick": "Kies een kantoor…",
  "impl.firms.empty": "Er zijn nog geen advieskantoren in de directory. Kantoren worden door de beheerder (super-tenant) toegevoegd.",
  "impl.engagements": "Ingeschakelde partners",
  "impl.none": "Nog geen implementatiepartners ingeschakeld.",
  "impl.consultants": "consultants",
  "impl.manage": "Consultants beheren",
  "impl.end": "Beëindigen",
  "impl.noConsultants": "Nog geen consultants uitgenodigd.",
  "impl.inviteEmail": "E-mailadres consultant",
  "impl.role": "Rol",
  "impl.role.consultant": "Consultant",
  "impl.role.firmAdmin": "Kantoorbeheerder",
  "impl.invite": "Uitnodigen",
  "tickset.title": "Ticket instellen",
  "tickset.sub": "Stel per tickettype een procedure-sjabloon in voor uw organisatie.",
  "tickset.empty": "Er zijn nog geen tickettypes beschikbaar. De Tickettype-bibliotheek wordt door de super-tenant (eigenaar/juridisch) beheerd; zodra tickettypes zijn toegevoegd, kunt u hier per type een procedure instellen.",
  "tickset.procedure": "Procedure-sjabloon",
  "tickset.placeholder": "Beschrijf de procedure voor dit tickettype…",
  "tickset.saved": "Opgeslagen.",
  // Mijn profiel (Gebruiker self-service; W28)
  "profile.title": "Mijn profiel",
  "profile.sub": "Beheer uw naam en taalvoorkeur. E-mail is uw inlogidentiteit (via WorkOS) en kan hier niet worden gewijzigd.",
  "profile.email": "E-mailadres",
  "profile.email.readonly": "Uw e-mailadres wordt beheerd door uw inlog (WorkOS) en is hier alleen-lezen.",
  "profile.fullName": "Volledige naam",
  "profile.preferredName": "Roepnaam",
  "profile.language": "Voorkeurstaal",
  "profile.saved": "Profiel opgeslagen.",
  "nav.functies": "Mijn functies",
  // Mijn functies (Gebruiker; W28)
  "functies.title": "Mijn functies",
  "functies.sub": "Uw actieve rollen per organisatie.",
  "functies.org": "Organisatie",
  "functies.role": "Rol",
  "functies.scope": "Bereik",
  "functies.status": "Status",
  "functies.empty": "U heeft nog geen actieve roltoewijzingen.",
  "functies.note": "Dit toont de platformrol per organisatie. De canonieke Functie/RACI-laag (zoals Functionaris gegevensbescherming) is door de eigenaar/juridisch beheerde content en wordt in een latere ronde gekoppeld.",
  "team.title": "Teamleden",
  "team.intro": "Nodig collega's uit en beheer hun rollen. Genodigden loggen in met WorkOS en worden via hun e-mail aan deze tenant gekoppeld (geen e-mail verzonden).",
  "team.invite": "Gebruiker uitnodigen",
  "team.email": "E-mail",
  "team.role": "Rol",
  "team.status": "Status",
  "team.send": "Uitnodigen",
  "team.invited": "Uitgenodigd.",
  "team.empty": "Nog geen teamleden.",
  "team.revoke": "Intrekken",
  "team.active": "actief",
  "team.pending": "in afwachting",
  "team.you": "jij",
  "nav.settings": "Instellingen",
  "settings.title": "Instellingen",
  "settings.name": "Naam van de tenant",
  "settings.language": "Standaardtaal",
  "settings.class": "Tenantklasse",
  "settings.saved": "Opgeslagen.",
  "settings.readonly": "Alleen-lezen toegang.",
  "common.language": "Taal",
  "common.loading": "Laden…",
  "common.save": "Opslaan",
  "common.cancel": "Annuleren",
  "common.create": "Aanmaken",
  "crud.updating": "bijwerken…",
  "crud.new": "+ Nieuw",
  "crud.newItem": "Nieuw item",
  "crud.creating": "Aanmaken…",
  "crud.failed": "Laden mislukt:",
  "crud.edit": "Bewerken",
  "crud.close": "Sluiten",
  "crud.deactivate": "Deactiveren",
  "crud.sure": "zeker?",
  "crud.yes": "ja",
  "crud.no": "nee",
  "crud.saved": "Opgeslagen.",
  "crud.readonly": "Alleen-lezen toegang.",
  "crud.saveChanges": "Wijzigingen opslaan",
  "crud.saving": "Opslaan…",
  "crud.select": "Selecteer…",
  "crud.history": "Wijzigingshistorie",
  "crud.search": "Zoeken…",
  "crud.noMatches": "Geen resultaten voor",
  "crud.prev": "Vorige",
  "crud.next": "Volgende",
  "crud.pageOf": "Pagina {0} van {1}",
  "crud.countItems": "{0} items",
  "imp.banner": "Je bekijkt {0} als operator — alleen-lezen",
  "imp.end": "Beëindigen",
  "mandate.banner": "Je werkt namens {0} onder een mandaat",
  "mandate.actions": "toegestane acties", "mandate.modules": "modules", "mandate.full": "volledige scope",
  "imp.start": "Impersoneer een tenant (operator)",
  "imp.tenantId": "Tenant-ID",
  "imp.reason": "Reden (geregistreerd)",
  "imp.go": "Start impersonatie",
  "page.verwerkingen": "Verwerkingen",
  "verw.none": "Nog geen verwerkingsactiviteiten.",
  "verw.search": "Zoek verwerkingen…",
  "verw.new": "+ Nieuwe verwerking",
  "verw.noMatches": "Geen resultaten voor",
  "verw.sub": "Verwerkingenregister — AVG art. 30",
  "verw.add": "Toevoegen",
  "verw.col.name": "Naam", "verw.col.status": "Status", "verw.col.heightenedRisk": "Verhoogd risico",
  "verw.col.incidental": "Incidenteel", "verw.col.controller": "Verantwoordelijke", "verw.col.processor": "Verwerker",
  "verw.col.effectiveFrom": "Actief sinds",
  "verw.field.effectiveFrom": "Actief sinds",
  "verw.starter": "Gouden Standaard toepassen",
  "verw.starter.applying": "Bezig met toepassen…",
  "verw.starter.hint": "Voeg de 11 standaard verwerkingen toe als startpunt voor uw register.",
  // L8 — party role-views
  "party.controllers": "Verwerkingsverantwoordelijken", "party.processors": "Verwerkers", "party.recipients": "Ontvangers",
  "party.jointControllers": "Joint controllers", "party.contacts": "Contactpersonen",
  // L6/L7 — Gebruiker surfaces
  "me.tickets.title": "Mijn tickets", "me.tickets.sub": "Tickets die aan u zijn toegewezen.",
  "me.tickets.empty": "Geen tickets aan u toegewezen.", "me.tickets.col.title": "Titel", "me.tickets.col.status": "Status",
  "me.tickets.col.priority": "Prioriteit", "me.tickets.col.due": "Deadline",
  "me.agr.title": "Mijn overeenkomsten", "me.agr.sub": "Verwerkersovereenkomsten (Art.28) en gedeelde DPA's.",
  "me.agr.empty": "Nog geen overeenkomsten.", "me.agr.col.name": "Overeenkomst", "me.agr.col.status": "Status", "me.agr.col.updated": "Bijgewerkt",
  "me.consultant.title": "Mijn consultant", "me.consultant.sub": "Uw implementatiepartner / adviseur.",
  "me.consultant.none.title": "Geen actieve consultant", "me.consultant.none.body": "U heeft nog geen consultant-engagement. Bekijk de consultant-directory om er een te starten.", "me.consultant.browse": "Consultants bekijken",
  "me.cases.title": "Mijn cases", "me.cases.sub": "Uw supportcases.", "me.cases.empty": "Geen supportcases. Cases verschijnen hier zodra u een supportverzoek indient.",
  "verw.field.role.hint": "Een verwerking kan zowel verantwoordelijke als verwerker zijn — vink beide aan indien van toepassing.",
  "verw.create.title": "Nieuwe verwerking",
  "verw.create.desc": "Maak een concept aan en werk de Art. 30-details daarna verder uit.",
  "verw.field.name": "Naam",
  "verw.field.description": "Omschrijving",
  "verw.field.heightenedRisk": "Verhoogd risico",
  "verw.field.heightenedRisk.hint": "verwerking met verhoogd risico voor betrokkenen",
  "verw.field.incidental": "Incidenteel",
  "verw.field.incidental.hint": "eenmalige / niet-structurele verwerking",
  "verw.field.role": "Rol",
  "verw.role.controller": "Verwerkingsverantwoordelijke",
  "verw.role.processor": "Verwerker",
  "verw.save": "Opslaan",
  "verw.saveOpen": "Opslaan en openen",
  "verw.saving": "Opslaan…",
  // M3 — row edit + archive (the register was create+read-only)
  "verw.edit.title": "Verwerking bewerken",
  "verw.edit.desc": "Werk de Art. 30-velden van deze verwerking bij.",
  "verw.rowactions": "Acties",
  "verw.action.edit": "Bewerken",
  "verw.action.archive": "Archiveren",
  "verw.archive.title": "Verwerking archiveren?",
  "verw.archive.body": "De verwerking wordt op ‘gearchiveerd’ gezet. Het Art. 30-record blijft bewaard voor verantwoording, maar telt niet meer als actief. U kunt dit later terugzetten.",
  "verw.archive.confirm": "Archiveren",
  "verw.archive.archiving": "Archiveren…",
  "crud.cancel": "Annuleren",
  "verw.yes": "Ja", "verw.no": "Nee",
  "verw.detail.back": "Verwerkingen",
  "verw.detail.title": "Verwerking",
  "verw.detail.lineage": "Herkomst (canonieke activiteit)",
  "verw.detail.instance": "Instantie van canonieke activiteit",
  "verw.detail.freeStanding": "Vrijstaand",
  "verw.detail.noLineage": "Geen herkomst beschikbaar — deze verwerking is niet aan een canonieke activiteit gekoppeld.",
  "verw.detail.fields": "Velden",
  "verw.detail.activityType": "Activiteittype",
  "verw.detail.loadFailed": "Laden mislukt:",
  "page.risico": "Risico's",
  "page.risico.sub": "risicoregister",
  "inspector.risico.empty": "Nog geen risico's geregistreerd.",
  "inspector.risico.edit": "Risicoscores bewerken",
  "inspector.assets.sub": "Bedrijfsmiddelenregister",
  "inspector.assets.empty": "Nog geen bedrijfsmiddelen geregistreerd.",
  "inspector.assets.new": "Nieuw bedrijfsmiddel",
  "inspector.threats.sub": "Bedreigingenregister",
  "inspector.threats.empty": "Nog geen bedreigingen geregistreerd.",
  "inspector.threats.new": "Nieuwe bedreiging",
  // P3 i18n completeness — threat/asset register field + column labels, party kinds, doel-detail (was literal NL).
  "ins.threats.field.name": "Naam bedreiging", "ins.threats.field.category": "Bedreigingscategorie",
  "ins.threats.field.class": "Bedreigingsklasse", "ins.threats.field.probability": "Waarschijnlijkheid",
  "ins.threats.field.description": "Omschrijving risico's",
  "ins.threats.col.name": "Naam", "ins.threats.col.class": "Klasse", "ins.threats.col.category": "Categorie", "ins.threats.col.probability": "Waarschijnlijkheid",
  "ins.assets.field.name": "Bedrijfsmiddelnaam", "ins.assets.field.category": "Bedrijfsmiddelcategorie",
  "ins.assets.field.class": "Bedrijfsmiddelklasse", "ins.assets.field.vulnerability": "Kwetsbaarheid",
  "ins.assets.field.likelihood": "Kans", "ins.assets.field.description": "Omschrijving",
  "ins.assets.col.category": "Categorie", "ins.assets.col.class": "Klasse", "ins.assets.col.name": "Naam", "ins.assets.col.vulnerability": "Kwetsbaarheid", "ins.assets.col.likelihood": "Kans",
  "ins.parties.kind.controller": "Verwerkingsverantwoordelijke", "ins.parties.kind.processor": "Verwerker",
  "ins.parties.kind.recipient": "Ontvanger", "ins.parties.kind.joint_controller": "Joint controller",
  "ins.parties.kind.contact": "Contactpersoon", "ins.parties.kind.other": "Overig",
  "doel.detail.sub": "Doel — Art. 30-detail per verwerkingsdoel", "doel.fallbackTitle": "Doel",
  "doel.general": "Algemeen", "doel.name": "Naam", "doel.description": "Omschrijving",
  "doel.cat.pdc": "Categorieën gegevens", "doel.cat.subjects": "Betrokkenen", "doel.cat.legalBasis": "Wettelijke grondslag",
  "doel.special": "bijzonder", "doel.nothingLinked": "Nog niets gekoppeld.",
  "doel.recipients": "Ontvangers", "doel.recipients.empty": "Nog geen ontvangers.", "doel.recipients.placeholder": "Categorie ontvanger",
  "doel.risico": "Risico", "doel.risico.c": "Vertrouwelijkheid", "doel.risico.i": "Integriteit", "doel.risico.a": "Beschikbaarheid", "doel.risico.motiv": "Motivering",
  "doel.scale.very_low": "Zeer laag", "doel.scale.low": "Laag", "doel.scale.medium": "Gemiddeld", "doel.scale.high": "Hoog", "doel.scale.very_high": "Zeer hoog",
  // P3 per-screen contextuele help ("?"-affordance, legacy parity).
  "help.verwerkingen": "Uw AVG art. 30-register van verwerkingsactiviteiten. Maak per verwerking een record en werk de doelen, grondslag, verwerkers en ontvangers bij.",
  "help.regime": "Bepaal welke canonieke activiteiten voor uw organisatie gelden. Alleen ingeschakelde activiteiten tellen mee in de verantwoording.",
  "help.accountability": "Berekend overzicht van geldig bewijs per ingeschakelde activiteit (art. 5(2) AVG). Rood = ontbrekend bewijs op de peildatum.",
  "help.tickets": "DSR- en datalek-werkvoorraad met 5-assige prioritering. Filter op status en tickettype.",
  "help.dpia": "Gegevensbeschermingseffectbeoordelingen (art. 35 AVG). Stel handmatig op en exporteer via Print → PDF.",
  "help.reports": "Vooraf gedefinieerde, parameteriseerbare rapporten met export.",
  "help.evidence": "Bewijscontainers waarin u documenten koppelt die uw verantwoording onderbouwen.",
  "help.assets": "Register van bedrijfsmiddelen die in scope zijn voor risicobeoordeling.",
  "help.threats": "Bedreigingenregister — de bedreigingen die op bedrijfsmiddelen inwerken.",
  "help.risks": "Risicoregister; scoor waarschijnlijkheid en impact per risico.",
  "help.transfers": "Doorgifteregister (art. 46, Hoofdstuk V). Koppel een doorgiftemechanisme per verwerking.",
  "help.filingSystems": "Register van bestandssystemen, gekoppeld aan verwerkingen.",
  "help.regulations": "Activeer per tenant de wet- en regelgeving die op u van toepassing is.",
  "help.partners": "Eén partnerregister, getoond per rol (verwerkers, ontvangers, joint controllers, contactpersonen).",
  "help.tasks": "Taken voor het privacyteam, optioneel gekoppeld aan een ingeschakelde activiteit.",
  "help.actielijst": "Alle openstaande acties uit het hele platform, geprioriteerd.",
  "help.orgDetails": "Organisatiegegevens van de verwerkingsverantwoordelijke.",
  "help.vestigingen": "De vestigingsplaatsen van de organisatie (hoofdvestiging bepaalt de leidende toezichthouder).",
  "help.privacyteam": "Het privacyteam: leden en hun functies binnen de organisatie.",
  "help.jurisdictietaal": "Geef aan in welke jurisdicties uw organisatie actief is en welke taal per jurisdictie geldt.",
  "help.settings": "Instellingen van de organisatie en de werkruimte.",
  "help.team": "Gebruikers en rollen binnen deze werkruimte.",
  "help.reportsMission": "Eén beleidsdocument per organisatie, vrij te bewerken met optioneel sjabloon.",
  "inspector.transfers.sub": "Art. 46 doorgifteregister — Hoofdstuk V",
  "inspector.transfers.empty": "Nog geen doorgiften geregistreerd.",
  "inspector.transfers.new": "Nieuwe doorgifte",
  "inspector.transfers.edit": "Doorgifte bewerken",
  "inspector.transfers.mechanism": "Doorgiftemechanisme (Art. 46)",
  "inspector.transfers.linkedVerwerking": "Gekoppelde verwerking",
  "inspector.transfers.noMechanisms": "Nog geen doorgiftemechanismen — een beheerder voegt eerst SCC/BCR/adequaatheidsmechanismen toe in de canonieke bibliotheken.",
  "inspector.evidence.sub": "Bewijscontainers",
  "inspector.evidence.empty": "Nog geen bewijscontainers.",
  "inspector.evidence.new": "Nieuwe container",
  // Inspecteur · Bestandssystemen + Wet- en regelgeving (PLAN-W27 B)
  "inspector.filing.sub": "Bestandssystemenregister — gekoppeld aan verwerkingen",
  "inspector.filing.empty": "Nog geen bestandssystemen geregistreerd.",
  "inspector.filing.new": "Nieuw bestandssysteem",
  "regs.sub": "Per-tenant activering van wet- en regelgeving",
  "regs.col.name": "Naam",
  "regs.col.jurisdiction": "Jurisdictie",
  "regs.col.status": "Status",
  "regs.enabled": "Geactiveerd",
  "regs.disabled": "Niet actief",
  "regs.add": "Toevoegen",
  "regs.remove": "Verwijderen",
  "regs.saveFailed": "Wijzigen mislukt:",
  "regs.empty": "Nog geen wet- en regelgeving in de canonieke catalogus — de catalogus wordt gevuld zodra de eigenaar/juridisch adviseur regelgeving goedkeurt.",
  "verw.detail.fs.title": "Bestandssystemen",
  "verw.detail.fs.empty": "Nog geen bestandssystemen gekoppeld.",
  "verw.detail.fs.add": "Toevoegen",
  "verw.detail.fs.remove": "Verwijderen",
  "verw.detail.fs.noOptions": "Nog geen bestandssystemen in het register — maak ze eerst aan onder Inspecteur → Bestandssystemen.",
  "verw.detail.doelen.title": "Doelen",
  "verw.detail.doelen.empty": "Nog geen doelen — voeg een verwerkingsdoel toe.",
  "verw.detail.doelen.placeholder": "Naam van het doel",
  "verw.detail.doelen.add": "Doel toevoegen",
  "verw.detail.doelen.remove": "Verwijderen",
  "page.dpia": "DPIA's",
  "page.dpia.sub": "gegevensbeschermingseffectbeoordelingen — art. 35",
  "page.dsar": "Verzoeken van betrokkenen (DSAR)",
  "page.actielijst": "Centrale actielijst",
  "page.actielijst.sub": "alle openstaande acties geprioriteerd — Sprint 8",
  "al.source": "Bron", "al.item": "Actie", "al.status": "Status", "al.priority": "Prioriteit", "al.due": "Deadline", "al.overdue": "Te laat",
  "al.empty": "Geen openstaande acties. De lijst wordt dagelijks bijgewerkt.",
  "al.kind.ticket": "Ticket", "al.kind.risico": "Risico", "al.kind.security_measure_instance": "Maatregel",
  "al.kind.sprint_gate_decision": "Sprint-gate", "al.kind.change_trigger_event": "Wijziging",
  "page.auditors": "Auditors",
  "page.auditors.sub": "externe-auditoropdrachten — art. 5(2) verantwoording",
  "page.trust": "Trust Exchange",
  "regime.title": "Regime configureren",
  "regime.sub": "Kies welke canonieke activiteiten gelden voor deze organisatie",
  "regime.tab.all": "Alle activiteiten",
  "regime.tab.enabled": "Ingeschakelde activiteiten",
  "regime.tab.mine": "Mijn activiteiten",
  "regime.col.activity": "Activiteit",
  "regime.col.type": "Type",
  "regime.col.state": "Standaard",
  "regime.col.enable": "Inschakelen",
  "regime.type.verwerking": "Verwerking",
  "regime.type.organisatie": "Organisatie",
  "regime.type.beide": "Beide",
  "regime.default.on": "Standaard aan",
  "regime.default.optin": "Optioneel",
  "regime.section": "Sectie",
  "regime.empty": "Nog geen canonieke activiteiten beschikbaar",
  "regime.empty.enabled": "Nog geen activiteiten ingeschakeld voor deze organisatie.",
  "regime.empty.mine": "Geen activiteiten aan jou toegewezen.",
  "regime.failed": "Laden mislukt:",
  "regime.saveFailed": "Wijzigen mislukt:",
  // Planner — Activiteit detail (selecteerbare activiteit → drill-in)
  "activity.detail.back": "Terug naar regime",
  "activity.detail.hoofddoel": "Hoofddoel",
  "activity.detail.inOrde": "In orde",
  "activity.detail.ontbrekend": "Bewijs ontbreekt",
  "activity.detail.enabled": "Ingeschakeld in regime",
  "activity.detail.description": "Toelichting",
  "activity.detail.noDescription": "Geen toelichting vastgelegd voor deze activiteit.",
  "activity.detail.meansOfProof": "Bewijsmiddelen (bewijslast)",
  "activity.detail.meansEmpty": "Nog geen bewijsmiddelen gekoppeld aan deze activiteit.",
  "activity.detail.files": "bestand(en)",
  "activity.detail.notFound": "Activiteit niet gevonden.",
  "activity.detail.citation": "Wettelijke grondslag",
  "activity.detail.checklists": "Checklists",
  "activity.detail.checklistsEmpty": "Nog geen checklists vastgelegd voor deze activiteit.",
  "activity.detail.kind.applicability": "Van toepassing",
  "activity.detail.kind.compliance": "Naleving",
  "activity.detail.bewijsvoering": "Bewijsvoering",
  "activity.detail.proofEmpty": "Nog geen bewijsvoering vastgelegd voor deze activiteit.",
  "activity.detail.proof.initial": "Initieel bewijs",
  "activity.detail.proof.procedure": "Procedureel bewijs",
  // Enum labels (raw DB values → NL); unmapped values fall back to a humanised form (lib/enum-labels.ts).
  "enum.verwerkingStatus.design": "Ontwerp",
  "enum.verwerkingStatus.pbd_review": "PbD-review",
  "enum.verwerkingStatus.validated": "Gevalideerd",
  "enum.verwerkingStatus.active": "Actief",
  "enum.verwerkingStatus.under_review": "In beoordeling",
  "enum.verwerkingStatus.archived": "Gearchiveerd",
  "enum.actingOrgRole.controller": "Verwerkingsverantwoordelijke",
  "enum.actingOrgRole.processor": "Verwerker",
  "enum.actingOrgRole.joint_controller": "Gezamenlijke verwerkingsverantwoordelijke",
  "enum.ticketLifecycle.draft": "Concept",
  "enum.ticketLifecycle.open": "Open",
  "enum.ticketLifecycle.in_progress": "In behandeling",
  "enum.ticketLifecycle.blocked": "Geblokkeerd",
  "enum.ticketLifecycle.in_review": "In beoordeling",
  "enum.ticketLifecycle.done": "Afgerond",
  "enum.ticketLifecycle.cancelled": "Geannuleerd",
  "enum.ticketLifecycle.deferred": "Uitgesteld",
  // Risk-triad detail drill-in (Bedrijfsmiddel / Bedreiging / Risico)
  "risk.detail.backAssets": "Terug naar bedrijfsmiddelen",
  "risk.detail.backThreats": "Terug naar bedreigingen",
  "risk.detail.backRisks": "Terug naar risico's",
  "risk.detail.threatsFaced": "Bedreigingen voor dit bedrijfsmiddel",
  "risk.detail.assetsAffected": "Getroffen bedrijfsmiddelen",
  "risk.detail.risks": "Gekoppelde risico's",
  "risk.detail.noThreats": "Nog geen bedreigingen gekoppeld.",
  "risk.detail.noAssets": "Nog geen bedrijfsmiddelen gekoppeld.",
  "risk.detail.noRisks": "Nog geen risico's gekoppeld.",
  "risk.detail.inherent": "Inherent (kans × impact)",
  "risk.detail.residual": "Residueel (kans × impact)",
  "risk.f.category": "Categorie",
  "risk.f.assetClass": "Klasse",
  "risk.f.vulnerability": "Kwetsbaarheid",
  "risk.f.likelihood": "Kans",
  "risk.f.description": "Omschrijving",
  "risk.f.threatClass": "Bedreigingsklasse",
  "risk.f.probability": "Waarschijnlijkheid",
  "risk.f.verwerking": "Verwerking",
  "risk.f.threat": "Bedreiging",
  "risk.f.asset": "Bedrijfsmiddel",
  "risk.f.treatment": "Behandeling",
  // Inspecteur detail drill-ins (Bestandssysteem / Bewijscontainer / Regelgeving)
  "insp.detail.backFiling": "Terug naar bestandssystemen",
  "insp.detail.backEvidence": "Terug naar bewijsmiddelen",
  "insp.detail.backRegs": "Terug naar wet- en regelgeving",
  "insp.detail.fs.verwerkingen": "Verwerkingen die hierin opslaan",
  "insp.detail.fs.noVerwerkingen": "Nog geen verwerkingen gekoppeld.",
  "insp.detail.ev.files": "Bestanden",
  "insp.detail.ev.noFiles": "Nog geen bestanden geüpload.",
  "insp.detail.ev.activities": "Gekoppelde activiteiten / objecten",
  "insp.detail.ev.noActivities": "Nog niet gekoppeld.",
  "insp.detail.reg.translations": "Vertalingen",
  "insp.detail.reg.noTranslations": "Geen vertalingen.",
  "insp.detail.reg.status": "Status",
  "insp.detail.reg.enabled": "Ingeschakeld",
  "insp.detail.reg.disabled": "Uitgeschakeld",
  "insp.f.anchorKind": "Anker (soort)",
  "insp.f.anchorId": "Anker-id",
  "insp.f.shortCode": "Code",
  "insp.f.source": "Bron",
  "insp.f.language": "Taal",
  "insp.f.url": "Publicatie-URL",
  // Methodology detail drill-ins (Programma / Verbeterpunt)
  "method.detail.backPrograms": "Terug naar programma's",
  "method.detail.backVerbeter": "Terug naar verbeterpunten",
  "method.detail.gates": "Sprint-gate besluiten",
  "method.detail.noGates": "Nog geen sprint-gate besluiten.",
  "method.detail.assessments": "Voortgangsbeoordelingen",
  "method.detail.noAssessments": "Nog geen beoordelingen.",
  "method.f.lifecycle": "Levenscyclus-status",
  "method.f.startedAt": "Gestart op",
  "method.f.expectedAt": "Verwachte afronding",
  "method.f.sprint": "Sprint",
  "method.f.status": "Status",
  "method.f.sourceTier": "Bron-niveau",
  "method.detail.backGates": "Terug naar sprint-gate beslissingen",
  "method.detail.backTriggers": "Terug naar wijzigingssignalen",
  "method.detail.program": "Programma",
  "method.detail.events": "Cascade-gebeurtenissen",
  "method.detail.noEvents": "Nog geen gebeurtenissen.",
  "method.f.decision": "Beslissing",
  "method.f.decidedAt": "Besloten op",
  "method.f.rationale": "Onderbouwing",
  "method.f.summary": "Samenvatting",
  "method.f.sourceKind": "Bronsoort",
  "method.f.cascade": "Cascade-bereik",
  "method.f.fanOut": "Fan-out status",
  "method.f.detectedAt": "Gedetecteerd op",
  // Partner-role detail drill-in (Art.28 DPA chain + verwerking bindings)
  "partner.detail.back": "Terug naar partners",
  "partner.detail.agreements": "Verwerkersovereenkomsten (Art.28)",
  "partner.detail.noAgreements": "Nog geen overeenkomsten vastgelegd.",
  "partner.detail.verwerkingen": "Gekoppelde verwerkingen",
  "partner.detail.noVerwerkingen": "Nog niet aan verwerkingen gekoppeld.",
  "partner.detail.unsigned": "Niet ondertekend",
  "partner.detail.addAgreement": "Verwerkersovereenkomst toevoegen",
  "partner.detail.agreementRef": "Referentie",
  "partner.detail.sccBased": "Gebaseerd op SCC's (standaardcontractbepalingen)",
  "partner.detail.kind.processor": "Verwerker",
  "partner.detail.kind.recipient": "Ontvanger",
  "partner.detail.kind.joint_controller": "Gezamenlijke verwerkingsverantwoordelijke",
  "partner.f.role": "Rol",
  "partner.f.partyKind": "Soort partij",
  "partner.f.country": "Land",
  "partner.f.contactName": "Contactpersoon",
  "partner.f.contactEmail": "Contact-e-mail",
  // Archief (soft-delete recovery, Theme 2)
  "nav.archive": "Archief",
  "archive.sub": "Gedeactiveerde records — herstel een per ongeluk verwijderd item.",
  "archive.empty": "Geen gedeactiveerde records.",
  "archive.restore": "Herstellen",
  "archive.restoreFailed": "Herstellen mislukt:",
  "archive.col.kind": "Soort",
  "archive.col.item": "Item",
  "archive.col.deleted": "Gedeactiveerd op",
  "archive.col.action": "Actie",
  // Verwerking change-history + Meldingen (Theme 4)
  "verw.detail.history.title": "Wijzigingsgeschiedenis",
  "verw.detail.history.empty": "Nog geen opgeslagen versies.",
  "nav.notifications": "Meldingen",
  "notif.sub": "Meldingen voor jouw account.",
  "notif.unread": "Nieuw",
  "notif.markRead": "Markeer als gelezen",
  "notif.open": "Openen",
  // Beleidsmaker — singleton beleidsdocumenten (FRONTEND-BLUEPRINT §3.2; W27-A)
  "pol.mission.title": "Missie",
  "pol.mission.sub": "De voor de organisatie geformuleerde missie — één document per organisatie",
  "pol.conduct.title": "Gedragsregels",
  "pol.conduct.sub": "De gedragsregels van de organisatie — één document per organisatie",
  "pol.goals.title": "Hoofddoelplanning",
  "pol.goals.sub": "De hoofddoelplanning van de organisatie — één document per organisatie",
  "pol.goals.deferred": "De gestructureerde hoofddoelplanning (doelenlijst) is een open ontwerpvraag voor de eigenaar (DEFERRED-A3). Dit scherm gebruikt voorlopig hetzelfde vrije-tekstpatroon als Missie en Gedragsregels.",
  "pol.body.label": "Documenttekst",
  "pol.placeholder": "Nog geen tekst — schrijf hier het document of pas een template toe.",
  "pol.plaintext": "Platte tekst — rich-text opmaak (zoals in het legacysysteem) volgt in een latere ronde.",
  "pol.version": "Versie {0}",
  "pol.undo": "Wijzigingen ongedaan maken",
  "pol.saveFailed": "Opslaan mislukt:",
  "pol.template": "Template",
  "pol.template.title": "Template toepassen",
  "pol.template.warn": "Let op: het toepassen van een template OVERSCHRIJFT de huidige tekst van dit document. Klik daarna op Opslaan om de wijziging te bewaren, of op 'Wijzigingen ongedaan maken' om terug te gaan.",
  "pol.template.empty": "Er zijn nog geen templates beschikbaar. Canonieke beleids-templates worden door de super-tenant (eigenaar/juridisch) opgesteld; die bibliotheek is op dit moment leeg.",
  "pol.template.apply": "Toepassen (overschrijft)",
  "pol.template.applied": "Template toegepast — nog niet opgeslagen",
  // Controller · Verantwoording (COMPUTED accountability dashboard) — PLAN-W27 C; FRONTEND-BLUEPRINT §3.4
  "acc.title": "Verantwoording",
  "acc.sub": "Berekend verantwoordingsoverzicht — geldig bewijs per ingeschakelde activiteit (art. 5(2) AVG)",
  "acc.error": "Laden van het dashboard is mislukt:",
  "acc.asOf": "Peildatum",
  "acc.filter.beginMonth": "Begin maand",
  "acc.filter.beginYear": "Begin jaar",
  "acc.filter.endMonth": "Einde maand",
  "acc.filter.endYear": "Einde jaar",
  "acc.filter.hoofddoel": "Hoofddoel",
  "acc.filter.activity": "Activiteit",
  "acc.filter.all": "Alle",
  "acc.filter.apply": "Toepassen",
  "acc.kpi.pct": "Verantwoording",
  "acc.kpi.pct.hint": "percentage activiteiten in orde",
  "acc.kpi.inOrde": "In orde",
  "acc.kpi.inOrde.hint": "activiteiten met geldig bewijs / ingeschakeld",
  "acc.kpi.nonacc": "Non-accountability",
  "acc.kpi.nonacc.hint": "hiaten in uw verantwoordingsdocumentatie",
  "acc.kpi.missing": "Ontbrekende bewijzen",
  "acc.kpi.missing.hint": "bewijsvereisten zonder geldig document",
  "acc.sections.title": "Verantwoording per sectie",
  "acc.sections.sub": "Aandeel activiteiten in orde per hoofddoel-sectie.",
  "acc.sections.empty": "Geen secties in de selectie.",
  "acc.section": "Sectie",
  "acc.missing.title": "Ontbrekende bewijzen",
  "acc.missing.sub": "Activiteiten zonder geldig bewijsmiddel op de peildatum.",
  "acc.missing.empty": "Geen ontbrekende bewijzen. Alles is in orde.",
  "acc.missing.noContainer": "Geen bewijscontainer gekoppeld",
  "acc.col.code": "Code",
  "acc.col.activity": "Activiteit",
  "acc.col.container": "Bewijsmiddel",
  "acc.empty.title": "Nog geen activiteiten ingeschakeld",
  "acc.empty.body": "De verantwoording wordt berekend over de ingeschakelde activiteiten van het regime. Schakel activiteiten in om te beginnen.",
  "acc.empty.cta": "Regime configureren",
  "acc.note.onDemand": "Momentopname op de peildatum — de maandelijkse trendgrafiek (non-accountability door de tijd) volgt.",
  // Rapporten — report catalog (FRONTEND-BLUEPRINT §3.6)
  "page.reports": "Rapporten",
  "page.reports.sub": "vooraf gedefinieerde rapporten — parameteriseerbaar, met export",
  "rep.request": "Rapport opvragen",
  "rep.requesting": "Bezig…",
  "rep.soon": "binnenkort",
  "rep.format": "Formaat",
  "rep.status": "Status",
  "rep.period": "Periode",
  "rep.periodBegin": "Begin",
  "rep.periodEnd": "Einde",
  "rep.ticketType": "Tickettype",
  "rep.complexity": "Complexiteit",
  "rep.noParams": "Geen parameters.",
  "rep.failed": "Opvragen mislukt:",
  // status enum (e15) — Register van verwerkingen
  "rep.status.all": "Alle", "rep.status.new": "Nieuw", "rep.status.active": "Actief", "rep.status.inactive": "Inactief",
  // complexity enum (e14)
  "rep.cx.all": "Alle", "rep.cx.yes": "Ja", "rep.cx.no": "Nee",
  // 10 report titles + descriptions
  "rep.1.title": "Complexiteit van verwerkingen",
  "rep.1.desc": "Rangschikking van verwerkingen op complexiteit.",
  "rep.2.title": "Activiteitenplanning",
  "rep.2.desc": "Overzicht van de activiteitenplanning voor de hele organisatie.",
  "rep.3.title": "Mijn activiteitenplanning",
  "rep.3.desc": "Activiteitenplanning voor uzelf.",
  "rep.4.title": "Register van verwerkingen",
  "rep.4.desc": "Verwerkingenregister (art. 30) met algemene info, doeleinden, verwerkers en ontvangers.",
  "rep.5.title": "Register van bestandssystemen",
  "rep.5.desc": "Register van bestandssystemen met gekoppelde verwerkingen, locatie en doel.",
  "rep.5.note": "Exporteert het bestandssystemenregister als CSV.",
  "rep.6.title": "AVG-verantwoordingsstatus",
  "rep.6.desc": "AVG-verantwoordingsstatus: realisatiepercentages op drie niveaus.",
  "rep.6.note": "Berekend uit de Verantwoording (ingeschakelde activiteiten × geldig bewijs). De periode is een legacy-parameter; de huidige berekening is per heden.",
  "rep.7.title": "Ticket rapport",
  "rep.7.desc": "Overzicht van tickets per periode en tickettype.",
  "rep.8.title": "Privacyteam rapport",
  "rep.8.desc": "Overzicht van het privacyteam.",
  "rep.9.title": "Verwerkersrapport",
  "rep.9.desc": "Overzicht van de verwerkers.",
  "rep.10.title": "Privacyverklaring sjabloon",
  "rep.10.desc": "Privacyverklaring (art. 13/14) op basis van geselecteerde verwerkingen.",
  // TicketType enum (e30) — captured in §3.6 snapshot
  "rep.tt.inzage": "Verzoek tot inzage (art. 15)",
  "rep.tt.beperking": "Verzoek tot beperking (art. 18)",
  "rep.tt.wissing": "Verzoek tot gegevenswissing (art. 17)",
  "rep.tt.portabiliteit": "Verzoek tot dataportabiliteit (art. 20)",
  "rep.tt.intrekking": "Intrekking ouderlijke toestemming (art. 7/8)",
  "rep.tt.datalek": "Melding datalek (art. 33/34)",
  "rep.tt.klachten": "Klachten en bezwaren (art. 21/77)",
  "rep.tt.rectificatie": "Verzoek tot rectificatie (art. 16)",
  // Tickets persona — round-3 list + detail (PLAN-W27 D / FRONTEND-BLUEPRINT §3.5)
  "tk.sub": "DSR- en datalek-werkvoorraad met 5-assige prioritering",
  "tk.empty": "Nog geen tickets.",
  "tk.noMatches": "Geen tickets voor deze filters.",
  "tk.col.title": "Onderwerp",
  "tk.col.status": "Status",
  "tk.col.priority": "Prioriteit",
  "tk.col.kind": "Type",
  "tk.col.created": "Aangemaakt",
  "tk.col.due": "Deadline",
  "tk.filter.status": "Filter op status",
  "tk.filter.kind": "Filter op tickettype",
  "tk.filter.allStatuses": "Alle statussen",
  "tk.filter.allKinds": "Alle typen",
  "tk.kindsEmpty": "Tickettype-bibliotheek nog leeg (juridische review).",
  "tk.createBlocked": "Tickets aanmaken vereist de canonieke tickettype-bibliotheek; die inhoud wacht op juridische review. Bestaande tickets kunnen worden geopend, bewerkt (incl. 5-assige prioriteit) en gedeactiveerd.",
  "tk.new": "Nieuw ticket",
  "tk.detail.back": "Tickets",
  "tk.detail.title": "Ticket",
  "tk.detail.loadFailed": "Laden mislukt:",
  "tk.detail.notFound": "Ticket niet gevonden.",
  "tk.detail.edit": "Ticket bewerken",
  "tk.detail.workflow": "Status & workflow",
  "tk.detail.setStatus": "Zet status naar",
  "tk.detail.workflowNote": "Generieke ticket-lifecycle. Het typespecifieke workflowschema (state machine per tickettype) is juridisch te valideren inhoud en nog niet beschikbaar.",
  "tk.detail.priority": "Prioriteit (5 assen)",
  "tk.detail.composite": "Samengestelde score",
  "tk.detail.compositeHint": "De samengestelde score wordt automatisch herberekend zodra een as wijzigt.",
  "tk.detail.fields": "Gegevens",
  "tk.detail.noHistory": "Nog geen wijzigingshistorie.",
  "tk.axis.axisRegulatorySeverity": "Regelgevende ernst",
  "tk.axis.axisOperationalImpact": "Operationele impact",
  "tk.axis.axisImplementationEffort": "Implementatie-inspanning",
  "tk.axis.axisDependencyDepth": "Afhankelijkheidsdiepte",
  "tk.axis.axisDeadlineProximity": "Deadline-nabijheid",
  "tk.axis.unset": "niet gezet",
  "tk.scale.very_low": "Zeer laag",
  "tk.scale.low": "Laag",
  "tk.scale.medium": "Gemiddeld",
  "tk.scale.high": "Hoog",
  "tk.scale.very_high": "Zeer hoog",
  "tk.lc.draft": "Concept",
  "tk.lc.open": "Open",
  "tk.lc.in_progress": "In behandeling",
  "tk.lc.blocked": "Geblokkeerd",
  "tk.lc.in_review": "In review",
  "tk.lc.done": "Afgerond",
  "tk.lc.cancelled": "Geannuleerd",
  "tk.lc.deferred": "Uitgesteld",
  "tk.field.kind": "Tickettype (canoniek)",
  "tk.field.type": "Tickettype (migratie)",
  "tk.field.body": "Omschrijving",
  "tk.field.verwerking": "Gekoppelde verwerking",
  "tk.field.source": "Bron",
  "tk.field.assigned": "Toegewezen aan",
  "tk.field.language": "Taal",
  "tk.field.created": "Aangemaakt",
  "tk.field.due": "Deadline",
  "tk.field.resolved": "Opgelost",
  "tk.field.currentState": "Typespecifieke status",
};
const EN: Catalog = {
  "nav.dashboard": "Dashboard",
  "persona.inspecteur": "Inspector", "persona.beleidsmaker": "Policy-maker", "persona.planner": "Planner",
  "persona.controller": "Controller", "persona.tickets": "Tickets", "persona.rapporten": "Reports",
  "ctx.organisation": "Organisation", "ctx.regulation": "Regulation",
  "nav.account": "Account", "nav.logout": "Log out", "nav.overview": "Overview", "nav.accountability": "Accountability",
  "nav.reports": "Reports", "nav.mission": "Mission", "nav.conduct": "Code of conduct", "nav.goalPlanning": "Goal planning",
  "nav.regime": "Configure regime", "nav.tasks": "Tasks", "nav.filingSystems": "Filing systems", "nav.regulations": "Laws & regulations",
  "nav.verwerkingen": "Processing",
  "nav.activities": "Activities",
  "nav.assets": "Assets",
  "nav.threats": "Threats",
  "nav.risks": "Risks",
  "nav.dpia": "DPIA",
  "nav.evidence": "Evidence",
  "ev.files": "Files",
  "ev.upload": "Upload",
  "ev.uploading": "Uploading…",
  "ev.download": "Download",
  "ev.none": "No files yet.",
  "ev.maxSize": "max 25 MB",
  "nav.tickets": "Tickets",
  "nav.dsar": "DSAR",
  "nav.transfers": "Transfers",
  "nav.actielijst": "Action list",
  "nav.programs": "Programs",
  "nav.pdca": "PDCA",
  "nav.improvements": "Improvements",
  "nav.sprintGates": "Sprint gates",
  "nav.changeFeed": "Changes",
  "nav.switchTenant": "Switch tenant",
  "nav.billing": "Billing",
  "nav.trustExchange": "Trust Exchange",
  "nav.consultants": "Consultants",
  "nav.auditors": "Auditors",
  "dash.title": "Compliance cockpit",
  "dash.tenant": "tenant",
  "dash.verwerkingen.title": "Processing activities",
  "dash.verwerkingen.body": "Your GDPR Art. 30 register of processing activities.",
  "dash.tickets.title": "Tickets",
  "dash.tickets.body": "Open work items and compliance tasks.",
  "dash.deadlines.title": "Upcoming deadlines",
  "dash.deadlines.none": "No upcoming deadlines.",
  "dash.deadlines.overdue": "overdue",
  "notif.aria": "Notifications",
  "notif.title": "Notifications",
  "notif.empty": "No notifications.",
  "nav.partners": "Partners",
  "partners.title": "Partners & processors",
  "partners.sub": "Art. 28 processors + cross-tenant engagements",
  "partners.engagements": "Shared engagements",
  "partners.engagements.intro": "Two-party processor engagements; status becomes 'active' once both parties confirm.",
  "partners.engagements.none": "No engagements yet.",
  "partners.newEngagement": "+ New engagement",
  "partners.engagementName": "Engagement name",
  "partners.processorTenantId": "Processor tenant id (optional)",
  "partners.name": "Name",
  "partners.role": "Role",
  "partners.controller": "controller",
  "partners.processor": "processor",
  "partners.confirm": "Confirm",
  "partners.dpas": "Shared DPAs",
  "partners.newDpa": "+ New DPA",
  "partners.engagement": "Engagement",
  "partners.dpaVersion": "DPA version",
  "partners.dpas.needEngagement": "Create an engagement first.",
  "partners.family": "Art. 28 partner family",
  "partners.family.gated": "Creating partner roles needs the partner directory (not yet available). A read-only overview is shown below.",
  "partners.roles": "Partner roles",
  "partners.agreements": "Processor agreements",
  "partners.schedules": "Processing schedules",
  "partners.subProcessors": "Sub-processors",
  "partners.overview.none": "No items.",
  "ins.partners.title": "Partners",
  "ins.partners.sub": "One partner register, presented per role (controllers, processors, recipients, joint controllers, contact persons).",
  "ins.partners.addRole": "Add role",
  "ins.partners.noParties": "No external parties yet. Add one first via 'External parties' in the Inspector menu.",
  "nav.externalParties": "External parties",
  "ins.parties.title": "External parties",
  "ins.parties.empty": "No external parties yet. Add the organisations you share data with (processors, recipients, joint controllers, contacts).",
  "ins.parties.field.name": "Name",
  "ins.parties.field.legalName": "Legal name",
  "ins.parties.field.kind": "Party kind",
  "ins.parties.field.country": "Country",
  "ins.parties.field.contactName": "Contact person",
  "ins.parties.field.contactEmail": "Contact email",
  "ins.parties.field.notes": "Notes",
  "ins.partners.tab.controllers": "Controllers",
  "ins.partners.tab.processors": "Processors",
  "ins.partners.tab.recipients": "Recipients",
  "ins.partners.tab.jointControllers": "Joint controllers",
  "ins.partners.tab.contacts": "Contact persons",
  "ins.partners.col.partner": "Partner",
  "ins.partners.col.role": "Role",
  "ins.partners.empty.controllers": "No controllers yet.",
  "ins.partners.empty.processors": "No processors yet.",
  "ins.partners.empty.recipients": "No recipients yet.",
  "ins.partners.empty.jointControllers": "No joint controllers yet.",
  "ins.partners.empty.contacts": "No contact persons yet.",
  "ins.partners.failed": "Failed to load partners:",
  "ins.partners.role.controller": "Controller",
  "ins.partners.role.data_processor": "Processor",
  "ins.partners.role.data_recipient": "Recipient",
  "ins.partners.role.joint_controller": "Joint controller",
  "ins.partners.role.sub_processor": "Sub-processor",
  "ins.partners.role.consultancy_partner": "Consultancy partner",
  "ins.partners.role.auditor": "Auditor",
  "ins.partners.role.representative_art27": "Representative (Art. 27)",
  "ins.partners.role.dpo_art37": "Data protection officer (Art. 37)",
  "nav.team": "Team",
  "nav.orgDetails": "Organisation details",
  "nav.jurisdictietaal": "Jurisdiction & language",
  "jt.title": "Jurisdiction & language settings", "jt.sub": "Indicate which jurisdictions your organisation operates in and the content language per jurisdiction.",
  "jt.primary": "Primary", "jt.jurisdiction": "Jurisdiction", "jt.language": "Language", "jt.add": "Add jurisdiction",
  "jt.save": "Save", "jt.saved": "Jurisdiction & language settings saved.", "jt.none": "No jurisdictions available.",
  "nav.profile": "My profile",
  // Organisatiegegevens (Organisatie; W29)
  "org.title": "Organisation details",
  "org.sub": "Your organisation's details. Legal name, registration and VAT number are set by the administrator; you manage the trade name and contact details.",
  "org.legalName": "Legal name",
  "org.tradeName": "Trade name",
  "org.registration": "Registration no.",
  "org.vat": "VAT number",
  "org.readonlyNote": "Legal name, registration and VAT number are read-only (managed by the super-tenant).",
  "org.contact": "Contact details",
  "org.contact.address": "Address",
  "org.contact.postalCode": "Postal code",
  "org.contact.city": "City",
  "org.contact.country": "Country",
  "org.contact.email": "Email",
  "org.contact.phone": "Phone",
  "org.saved": "Organisation details saved.",
  "nav.vestigingen": "Establishments",
  "nav.privacyteam": "Privacy team",
  "crud.remove": "Remove",
  // Vestigingen (Organisatie; W29)
  "vest.title": "Establishments",
  "vest.sub": "Your organisation's establishments (sites/branches).",
  "vest.new": "New establishment",
  "vest.empty": "No establishments yet.",
  "vest.name": "Name",
  "vest.address": "Address",
  "vest.postalCode": "Postal code",
  "vest.city": "City",
  "vest.country": "Country",
  // Privacyteam (Organisatie; W29)
  "pt.title": "Privacy team",
  "pt.sub": "The privacy-team members and their function within the organisation.",
  "pt.member": "Member",
  "pt.email": "Email",
  "pt.functie": "Function",
  "pt.add": "Add member",
  "pt.pickUser": "Pick a user…",
  "pt.pickFunctie": "Pick a function…",
  "pt.empty": "No team members yet.",
  "pt.noFuncties": "No functions are available yet. The Function library is managed by the super-tenant (owner/counsel); once functions are added you can assign one to team members.",
  // Planner · Taken (tasks; W30)
  "taken.title": "Tasks",
  "taken.sub": "Tasks for the privacy team, optionally linked to an enabled activity.",
  "taken.new": "New task",
  "taken.empty": "No tasks yet.",
  "taken.taskTitle": "Title",
  "taken.description": "Description",
  "taken.activity": "Activity",
  "taken.assignee": "Assigned to",
  "taken.due": "Due date",
  "taken.status": "Status",
  "taken.pickActivity": "Pick an activity…",
  "taken.pickAssignee": "Pick a user…",
  "taken.st.open": "Open",
  "taken.st.in_progress": "In progress",
  "taken.st.done": "Done",
  "taken.st.cancelled": "Cancelled",
  // Organisatie · Ticket instellen (W30)
  "nav.ticketInstellen": "Ticket settings",
  "nav.implPartner": "Implementation partner",
  // Implementatiepartner onboarding (W37)
  "impl.title": "Implementation partner",
  "impl.sub": "Engage a consultancy firm as your implementation partner and invite its consultants. Invited consultants get partner-cockpit access scoped to your organisation.",
  "impl.signin": "Sign in as an admin to manage implementation partners.",
  "impl.engage": "Engage a firm",
  "impl.engage.sub": "Pick a firm from the directory and engage it as a partner.",
  "impl.engage.btn": "Engage",
  "impl.engaging": "Engaging…",
  "impl.firm": "Consultancy firm",
  "impl.firm.pick": "Pick a firm…",
  "impl.firms.empty": "No consultancy firms in the directory yet. Firms are added by the (super-tenant) administrator.",
  "impl.engagements": "Engaged partners",
  "impl.none": "No implementation partners engaged yet.",
  "impl.consultants": "consultants",
  "impl.manage": "Manage consultants",
  "impl.end": "End",
  "impl.noConsultants": "No consultants invited yet.",
  "impl.inviteEmail": "Consultant email",
  "impl.role": "Role",
  "impl.role.consultant": "Consultant",
  "impl.role.firmAdmin": "Firm admin",
  "impl.invite": "Invite",
  "tickset.title": "Ticket settings",
  "tickset.sub": "Configure a procedure template per ticket type for your organisation.",
  "tickset.empty": "No ticket types are available yet. The Ticket-type library is managed by the super-tenant (owner/counsel); once ticket types are added you can configure a procedure per type here.",
  "tickset.procedure": "Procedure template",
  "tickset.placeholder": "Describe the procedure for this ticket type…",
  "tickset.saved": "Saved.",
  // Mijn profiel (Gebruiker self-service; W28)
  "profile.title": "My profile",
  "profile.sub": "Manage your name and language preference. Email is your login identity (via WorkOS) and cannot be changed here.",
  "profile.email": "Email address",
  "profile.email.readonly": "Your email is managed by your login (WorkOS) and is read-only here.",
  "profile.fullName": "Full name",
  "profile.preferredName": "Preferred name",
  "profile.language": "Preferred language",
  "profile.saved": "Profile saved.",
  "nav.functies": "My roles",
  // Mijn functies (Gebruiker; W28)
  "functies.title": "My roles",
  "functies.sub": "Your active roles per organisation.",
  "functies.org": "Organisation",
  "functies.role": "Role",
  "functies.scope": "Scope",
  "functies.status": "Status",
  "functies.empty": "You have no active role assignments yet.",
  "functies.note": "This shows the platform role per organisation. The canonical Functie/RACI layer (e.g. Data protection officer) is owner/counsel-managed content and will be linked in a later round.",
  "team.title": "Team members",
  "team.intro": "Invite colleagues and manage their roles. Invitees sign in with WorkOS and are linked to this tenant by their email (no email is sent).",
  "team.invite": "Invite a user",
  "team.email": "Email",
  "team.role": "Role",
  "team.status": "Status",
  "team.send": "Invite",
  "team.invited": "Invited.",
  "team.empty": "No team members yet.",
  "team.revoke": "Revoke",
  "team.active": "active",
  "team.pending": "pending",
  "team.you": "you",
  "nav.settings": "Settings",
  "settings.title": "Settings",
  "settings.name": "Tenant name",
  "settings.language": "Default language",
  "settings.class": "Tenant class",
  "settings.saved": "Saved.",
  "settings.readonly": "Read-only access.",
  "common.language": "Language",
  "common.loading": "Loading…",
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.create": "Create",
  "crud.updating": "updating…",
  "crud.new": "+ New",
  "crud.newItem": "New item",
  "crud.creating": "Creating…",
  "crud.failed": "Failed to load:",
  "crud.edit": "Edit",
  "crud.close": "Close",
  "crud.deactivate": "Deactivate",
  "crud.sure": "sure?",
  "crud.yes": "yes",
  "crud.no": "no",
  "crud.saved": "Saved.",
  "crud.readonly": "Read-only access.",
  "crud.saveChanges": "Save changes",
  "crud.saving": "Saving…",
  "crud.select": "Select…",
  "crud.history": "Change history",
  "crud.search": "Search…",
  "crud.noMatches": "No matches for",
  "crud.prev": "Previous",
  "crud.next": "Next",
  "crud.pageOf": "Page {0} of {1}",
  "crud.countItems": "{0} items",
  "imp.banner": "You are viewing {0} as an operator — read-only",
  "imp.end": "End",
  "mandate.banner": "You are acting for {0} under a mandate",
  "mandate.actions": "allowed actions", "mandate.modules": "modules", "mandate.full": "full scope",
  "imp.start": "Impersonate a tenant (operator)",
  "imp.tenantId": "Tenant ID",
  "imp.reason": "Reason (audited)",
  "imp.go": "Start impersonation",
  "page.verwerkingen": "Processing activities",
  "verw.none": "No processing activities yet.",
  "verw.search": "Search processing activities…",
  "verw.new": "+ New verwerking",
  "verw.noMatches": "No matches for",
  "verw.sub": "Register of processing activities — GDPR Art. 30",
  "verw.add": "Add",
  "verw.col.name": "Name", "verw.col.status": "Status", "verw.col.heightenedRisk": "Heightened risk",
  "verw.col.incidental": "Incidental", "verw.col.controller": "Controller", "verw.col.processor": "Processor",
  "verw.col.effectiveFrom": "Active since",
  "verw.field.effectiveFrom": "Active since",
  "verw.starter": "Apply Gouden Standaard",
  "verw.starter.applying": "Applying…",
  "verw.starter.hint": "Add the 11 baseline processings as a starting point for your register.",
  // L8 — party role-views
  "party.controllers": "Controllers", "party.processors": "Processors", "party.recipients": "Recipients",
  "party.jointControllers": "Joint controllers", "party.contacts": "Contacts",
  // L6/L7 — user surfaces
  "me.tickets.title": "My tickets", "me.tickets.sub": "Tickets assigned to you.",
  "me.tickets.empty": "No tickets assigned to you.", "me.tickets.col.title": "Title", "me.tickets.col.status": "Status",
  "me.tickets.col.priority": "Priority", "me.tickets.col.due": "Due",
  "me.agr.title": "My agreements", "me.agr.sub": "Processor agreements (Art.28) and shared DPAs.",
  "me.agr.empty": "No agreements yet.", "me.agr.col.name": "Agreement", "me.agr.col.status": "Status", "me.agr.col.updated": "Updated",
  "me.consultant.title": "My consultant", "me.consultant.sub": "Your implementation partner / advisor.",
  "me.consultant.none.title": "No active consultant", "me.consultant.none.body": "You don't have a consultant engagement yet. Browse the consultant directory to start one.", "me.consultant.browse": "Browse consultants",
  "me.cases.title": "My cases", "me.cases.sub": "Your support cases.", "me.cases.empty": "No support cases. Cases appear here once you raise a support request.",
  "verw.field.role.hint": "A processing can be both controller and processor — tick both where applicable.",
  "verw.create.title": "New processing activity",
  "verw.create.desc": "Create a draft, then flesh out the Art. 30 details.",
  "verw.field.name": "Name",
  "verw.field.description": "Description",
  "verw.field.heightenedRisk": "Heightened risk",
  "verw.field.heightenedRisk.hint": "processing with heightened risk to data subjects",
  "verw.field.incidental": "Incidental",
  "verw.field.incidental.hint": "one-off / non-structural processing",
  "verw.field.role": "Role",
  "verw.role.controller": "Controller",
  "verw.role.processor": "Processor",
  "verw.save": "Save",
  "verw.saveOpen": "Save and open",
  "verw.saving": "Saving…",
  // M3 — row edit + archive
  "verw.edit.title": "Edit processing activity",
  "verw.edit.desc": "Update the Art. 30 fields of this processing activity.",
  "verw.rowactions": "Actions",
  "verw.action.edit": "Edit",
  "verw.action.archive": "Archive",
  "verw.archive.title": "Archive this processing activity?",
  "verw.archive.body": "The activity is set to ‘archived’. The Art. 30 record is retained for accountability but no longer counts as active. You can restore it later.",
  "verw.archive.confirm": "Archive",
  "verw.archive.archiving": "Archiving…",
  "crud.cancel": "Cancel",
  "verw.yes": "Yes", "verw.no": "No",
  "verw.detail.back": "Processing activities",
  "verw.detail.title": "Processing activity",
  "verw.detail.lineage": "Lineage (canonical activity)",
  "verw.detail.instance": "Instance of canonical activity",
  "verw.detail.freeStanding": "Free-standing",
  "verw.detail.noLineage": "No lineage available — this processing activity is not bound to a canonical activity.",
  "verw.detail.fields": "Fields",
  "verw.detail.activityType": "Activity type",
  "verw.detail.loadFailed": "Failed to load:",
  "page.risico": "Risks",
  "page.risico.sub": "risk register",
  "inspector.risico.empty": "No risks registered yet.",
  "inspector.risico.edit": "Edit risk scores",
  "inspector.assets.sub": "Business-asset register",
  "inspector.assets.empty": "No assets registered yet.",
  "inspector.assets.new": "New asset",
  "inspector.threats.sub": "Threat register",
  "inspector.threats.empty": "No threats registered yet.",
  "inspector.threats.new": "New threat",
  // P3 i18n completeness (EN parity).
  "ins.threats.field.name": "Threat name", "ins.threats.field.category": "Threat category",
  "ins.threats.field.class": "Threat class", "ins.threats.field.probability": "Probability",
  "ins.threats.field.description": "Description of risks",
  "ins.threats.col.name": "Name", "ins.threats.col.class": "Class", "ins.threats.col.category": "Category", "ins.threats.col.probability": "Probability",
  "ins.assets.field.name": "Asset name", "ins.assets.field.category": "Asset category",
  "ins.assets.field.class": "Asset class", "ins.assets.field.vulnerability": "Vulnerability",
  "ins.assets.field.likelihood": "Likelihood", "ins.assets.field.description": "Description",
  "ins.assets.col.category": "Category", "ins.assets.col.class": "Class", "ins.assets.col.name": "Name", "ins.assets.col.vulnerability": "Vulnerability", "ins.assets.col.likelihood": "Likelihood",
  "ins.parties.kind.controller": "Controller", "ins.parties.kind.processor": "Processor",
  "ins.parties.kind.recipient": "Recipient", "ins.parties.kind.joint_controller": "Joint controller",
  "ins.parties.kind.contact": "Contact person", "ins.parties.kind.other": "Other",
  "doel.detail.sub": "Purpose — Art. 30 detail per processing purpose", "doel.fallbackTitle": "Purpose",
  "doel.general": "General", "doel.name": "Name", "doel.description": "Description",
  "doel.cat.pdc": "Data categories", "doel.cat.subjects": "Data subjects", "doel.cat.legalBasis": "Legal basis",
  "doel.special": "special", "doel.nothingLinked": "Nothing linked yet.",
  "doel.recipients": "Recipients", "doel.recipients.empty": "No recipients yet.", "doel.recipients.placeholder": "Recipient category",
  "doel.risico": "Risk", "doel.risico.c": "Confidentiality", "doel.risico.i": "Integrity", "doel.risico.a": "Availability", "doel.risico.motiv": "Rationale",
  "doel.scale.very_low": "Very low", "doel.scale.low": "Low", "doel.scale.medium": "Medium", "doel.scale.high": "High", "doel.scale.very_high": "Very high",
  // P3 per-screen contextual help (EN parity).
  "help.verwerkingen": "Your GDPR Art. 30 register of processing activities. Create a record per processing and maintain purposes, legal basis, processors and recipients.",
  "help.regime": "Choose which canonical activities apply to your organisation. Only enabled activities count toward accountability.",
  "help.accountability": "Computed overview of valid evidence per enabled activity (Art. 5(2) GDPR). Red = missing evidence on the reference date.",
  "help.tickets": "DSR and data-breach work queue with 5-axis prioritisation. Filter by status and ticket type.",
  "help.dpia": "Data-protection impact assessments (Art. 35 GDPR). Author manually and export via Print → PDF.",
  "help.reports": "Pre-defined, parameterised reports with export.",
  "help.evidence": "Evidence containers where you link the documents underpinning your accountability.",
  "help.assets": "Register of business assets in scope for risk assessment.",
  "help.threats": "Threat register — the threats acting on assets.",
  "help.risks": "Risk register; score likelihood and impact per risk.",
  "help.transfers": "Transfer register (Art. 46, Chapter V). Link a transfer mechanism per processing.",
  "help.filingSystems": "Filing-systems register, linked to processing activities.",
  "help.regulations": "Per-tenant activation of the laws & regulations that apply to you.",
  "help.partners": "One partner register, presented per role (processors, recipients, joint controllers, contacts).",
  "help.tasks": "Tasks for the privacy team, optionally linked to an enabled activity.",
  "help.actielijst": "All open actions platform-wide, prioritised.",
  "help.orgDetails": "Master data of the controller organisation.",
  "help.vestigingen": "The organisation's establishments (the main establishment drives the lead supervisory authority).",
  "help.privacyteam": "The privacy team: members and their functions within the organisation.",
  "help.jurisdictietaal": "Indicate which jurisdictions your organisation operates in and the content language per jurisdiction.",
  "help.settings": "Organisation and workspace settings.",
  "help.team": "Users and roles in this workspace.",
  "help.reportsMission": "A single per-organisation policy document, freely editable with an optional template.",
  "inspector.transfers.sub": "Art. 46 transfer register — Chapter V",
  "inspector.transfers.empty": "No transfers registered yet.",
  "inspector.transfers.new": "New transfer",
  "inspector.transfers.edit": "Edit transfer",
  "inspector.transfers.mechanism": "Transfer mechanism (Art. 46)",
  "inspector.transfers.linkedVerwerking": "Linked processing activity",
  "inspector.transfers.noMechanisms": "No transfer mechanisms defined yet — an admin adds SCC/BCR/adequacy mechanisms in the canonical libraries first.",
  "inspector.evidence.sub": "Evidence containers",
  "inspector.evidence.empty": "No evidence containers yet.",
  "inspector.evidence.new": "New container",
  // Inspecteur · Bestandssystemen + Wet- en regelgeving (PLAN-W27 B)
  "inspector.filing.sub": "Filing-systems register — linked to processing activities",
  "inspector.filing.empty": "No filing systems registered yet.",
  "inspector.filing.new": "New filing system",
  "regs.sub": "Per-tenant activation of laws & regulations",
  "regs.col.name": "Name",
  "regs.col.jurisdiction": "Jurisdiction",
  "regs.col.status": "Status",
  "regs.enabled": "Enabled",
  "regs.disabled": "Not enabled",
  "regs.add": "Add",
  "regs.remove": "Remove",
  "regs.saveFailed": "Update failed:",
  "regs.empty": "No laws & regulations in the canonical catalog yet — the catalog fills as the owner/counsel approves regulations.",
  "verw.detail.fs.title": "Filing systems",
  "verw.detail.fs.empty": "No filing systems linked yet.",
  "verw.detail.fs.add": "Add",
  "verw.detail.fs.remove": "Remove",
  "verw.detail.fs.noOptions": "No filing systems in the register yet — create them first under Inspector → Filing systems.",
  "verw.detail.doelen.title": "Purposes",
  "verw.detail.doelen.empty": "No purposes yet — add a processing purpose.",
  "verw.detail.doelen.placeholder": "Purpose name",
  "verw.detail.doelen.add": "Add purpose",
  "verw.detail.doelen.remove": "Remove",
  "page.dpia": "DPIA",
  "page.dpia.sub": "data protection impact assessments — Art. 35",
  "page.dsar": "Data-subject requests (DSAR)",
  "page.actielijst": "Central action list",
  "page.actielijst.sub": "all open actions, prioritised — Sprint 8",
  "al.source": "Source", "al.item": "Action", "al.status": "Status", "al.priority": "Priority", "al.due": "Due", "al.overdue": "Overdue",
  "al.empty": "No open actions. The list refreshes daily.",
  "al.kind.ticket": "Ticket", "al.kind.risico": "Risk", "al.kind.security_measure_instance": "Measure",
  "al.kind.sprint_gate_decision": "Sprint gate", "al.kind.change_trigger_event": "Change",
  "page.auditors": "Auditors",
  "page.auditors.sub": "external-auditor engagements — Art. 5(2) accountability",
  "page.trust": "Trust Exchange",
  "regime.title": "Configure regime",
  "regime.sub": "Choose which canonical activities apply to this organisation",
  "regime.tab.all": "All activities",
  "regime.tab.enabled": "Enabled activities",
  "regime.tab.mine": "My activities",
  "regime.col.activity": "Activity",
  "regime.col.type": "Type",
  "regime.col.state": "Default",
  "regime.col.enable": "Enable",
  "regime.type.verwerking": "Processing",
  "regime.type.organisatie": "Organisation",
  "regime.type.beide": "Both",
  "regime.default.on": "Default on",
  "regime.default.optin": "Opt-in",
  "regime.section": "Section",
  "regime.empty": "No canonical activities available yet",
  "regime.empty.enabled": "No activities enabled for this organisation yet.",
  "regime.empty.mine": "No activities assigned to you.",
  "regime.failed": "Failed to load:",
  "regime.saveFailed": "Update failed:",
  // Planner — Activity detail (selectable activity → drill-in)
  "activity.detail.back": "Back to regime",
  "activity.detail.hoofddoel": "Main objective",
  "activity.detail.inOrde": "In order",
  "activity.detail.ontbrekend": "Evidence missing",
  "activity.detail.enabled": "Enabled in regime",
  "activity.detail.description": "Description",
  "activity.detail.noDescription": "No description recorded for this activity.",
  "activity.detail.meansOfProof": "Means of proof",
  "activity.detail.meansEmpty": "No means of proof linked to this activity yet.",
  "activity.detail.files": "file(s)",
  "activity.detail.notFound": "Activity not found.",
  "activity.detail.citation": "Legal basis",
  "activity.detail.checklists": "Checklists",
  "activity.detail.checklistsEmpty": "No checklists recorded for this activity yet.",
  "activity.detail.kind.applicability": "Applicability",
  "activity.detail.kind.compliance": "Compliance",
  "activity.detail.bewijsvoering": "Means of proof requirements",
  "activity.detail.proofEmpty": "No means-of-proof requirements recorded for this activity yet.",
  "activity.detail.proof.initial": "Initial proof",
  "activity.detail.proof.procedure": "Procedural proof",
  // Enum labels (raw DB values → EN); unmapped values fall back to a humanised form (lib/enum-labels.ts).
  "enum.verwerkingStatus.design": "Design",
  "enum.verwerkingStatus.pbd_review": "PbD review",
  "enum.verwerkingStatus.validated": "Validated",
  "enum.verwerkingStatus.active": "Active",
  "enum.verwerkingStatus.under_review": "Under review",
  "enum.verwerkingStatus.archived": "Archived",
  "enum.actingOrgRole.controller": "Controller",
  "enum.actingOrgRole.processor": "Processor",
  "enum.actingOrgRole.joint_controller": "Joint controller",
  "enum.ticketLifecycle.draft": "Draft",
  "enum.ticketLifecycle.open": "Open",
  "enum.ticketLifecycle.in_progress": "In progress",
  "enum.ticketLifecycle.blocked": "Blocked",
  "enum.ticketLifecycle.in_review": "In review",
  "enum.ticketLifecycle.done": "Done",
  "enum.ticketLifecycle.cancelled": "Cancelled",
  "enum.ticketLifecycle.deferred": "Deferred",
  // Risk-triad detail drill-in (Asset / Threat / Risk)
  "risk.detail.backAssets": "Back to assets",
  "risk.detail.backThreats": "Back to threats",
  "risk.detail.backRisks": "Back to risks",
  "risk.detail.threatsFaced": "Threats facing this asset",
  "risk.detail.assetsAffected": "Affected assets",
  "risk.detail.risks": "Linked risks",
  "risk.detail.noThreats": "No threats linked yet.",
  "risk.detail.noAssets": "No assets linked yet.",
  "risk.detail.noRisks": "No risks linked yet.",
  "risk.detail.inherent": "Inherent (likelihood × impact)",
  "risk.detail.residual": "Residual (likelihood × impact)",
  "risk.f.category": "Category",
  "risk.f.assetClass": "Class",
  "risk.f.vulnerability": "Vulnerability",
  "risk.f.likelihood": "Likelihood",
  "risk.f.description": "Description",
  "risk.f.threatClass": "Threat class",
  "risk.f.probability": "Probability",
  "risk.f.verwerking": "Processing",
  "risk.f.threat": "Threat",
  "risk.f.asset": "Asset",
  "risk.f.treatment": "Treatment",
  // Inspecteur detail drill-ins (Filing system / Evidence container / Regulation)
  "insp.detail.backFiling": "Back to filing systems",
  "insp.detail.backEvidence": "Back to evidence",
  "insp.detail.backRegs": "Back to regulations",
  "insp.detail.fs.verwerkingen": "Processings that store data here",
  "insp.detail.fs.noVerwerkingen": "No processings linked yet.",
  "insp.detail.ev.files": "Files",
  "insp.detail.ev.noFiles": "No files uploaded yet.",
  "insp.detail.ev.activities": "Linked activities / objects",
  "insp.detail.ev.noActivities": "Not linked yet.",
  "insp.detail.reg.translations": "Translations",
  "insp.detail.reg.noTranslations": "No translations.",
  "insp.detail.reg.status": "Status",
  "insp.detail.reg.enabled": "Enabled",
  "insp.detail.reg.disabled": "Disabled",
  "insp.f.anchorKind": "Anchor (kind)",
  "insp.f.anchorId": "Anchor id",
  "insp.f.shortCode": "Code",
  "insp.f.source": "Source",
  "insp.f.language": "Language",
  "insp.f.url": "Publication URL",
  // Methodology detail drill-ins (Program / Improvement point)
  "method.detail.backPrograms": "Back to programmes",
  "method.detail.backVerbeter": "Back to improvement points",
  "method.detail.gates": "Sprint-gate decisions",
  "method.detail.noGates": "No sprint-gate decisions yet.",
  "method.detail.assessments": "Progress assessments",
  "method.detail.noAssessments": "No assessments yet.",
  "method.f.lifecycle": "Lifecycle status",
  "method.f.startedAt": "Started at",
  "method.f.expectedAt": "Expected completion",
  "method.f.sprint": "Sprint",
  "method.f.status": "Status",
  "method.f.sourceTier": "Source tier",
  "method.detail.backGates": "Back to sprint-gate decisions",
  "method.detail.backTriggers": "Back to change triggers",
  "method.detail.program": "Programme",
  "method.detail.events": "Cascade events",
  "method.detail.noEvents": "No events yet.",
  "method.f.decision": "Decision",
  "method.f.decidedAt": "Decided at",
  "method.f.rationale": "Rationale",
  "method.f.summary": "Summary",
  "method.f.sourceKind": "Source kind",
  "method.f.cascade": "Cascade scope",
  "method.f.fanOut": "Fan-out status",
  "method.f.detectedAt": "Detected at",
  // Partner-role detail drill-in (Art.28 DPA chain + processing bindings)
  "partner.detail.back": "Back to partners",
  "partner.detail.agreements": "Processor agreements (Art.28)",
  "partner.detail.noAgreements": "No agreements recorded yet.",
  "partner.detail.verwerkingen": "Linked processings",
  "partner.detail.noVerwerkingen": "Not bound to any processings yet.",
  "partner.detail.unsigned": "Unsigned",
  "partner.detail.addAgreement": "Add processor agreement",
  "partner.detail.agreementRef": "Reference",
  "partner.detail.sccBased": "Based on SCCs (standard contractual clauses)",
  "partner.detail.kind.processor": "Processor",
  "partner.detail.kind.recipient": "Recipient",
  "partner.detail.kind.joint_controller": "Joint controller",
  "partner.f.role": "Role",
  "partner.f.partyKind": "Party kind",
  "partner.f.country": "Country",
  "partner.f.contactName": "Contact name",
  "partner.f.contactEmail": "Contact email",
  // Archive (soft-delete recovery, Theme 2)
  "nav.archive": "Archive",
  "archive.sub": "Deactivated records — restore an item that was removed by mistake.",
  "archive.empty": "No deactivated records.",
  "archive.restore": "Restore",
  "archive.restoreFailed": "Restore failed:",
  "archive.col.kind": "Kind",
  "archive.col.item": "Item",
  "archive.col.deleted": "Deactivated at",
  "archive.col.action": "Action",
  // Verwerking change-history + Notifications (Theme 4)
  "verw.detail.history.title": "Change history",
  "verw.detail.history.empty": "No saved versions yet.",
  "nav.notifications": "Notifications",
  "notif.sub": "Notifications for your account.",
  "notif.unread": "New",
  "notif.markRead": "Mark as read",
  "notif.open": "Open",
  // Beleidsmaker — singleton policy documents (FRONTEND-BLUEPRINT §3.2; W27-A)
  "pol.mission.title": "Mission",
  "pol.mission.sub": "The organisation's mission statement — one document per organisation",
  "pol.conduct.title": "Code of conduct",
  "pol.conduct.sub": "The organisation's code of conduct — one document per organisation",
  "pol.goals.title": "Goal planning",
  "pol.goals.sub": "The organisation's goal planning — one document per organisation",
  "pol.goals.deferred": "The structured goal-planning variant (goal list) is an open owner design question (DEFERRED-A3). For now this screen uses the same free-text pattern as Mission and Code of conduct.",
  "pol.body.label": "Document text",
  "pol.placeholder": "No text yet — write the document here or apply a template.",
  "pol.plaintext": "Plain text — rich-text formatting (as in the legacy system) follows in a later round.",
  "pol.version": "Version {0}",
  "pol.undo": "Undo changes",
  "pol.saveFailed": "Save failed:",
  "pol.template": "Template",
  "pol.template.title": "Apply a template",
  "pol.template.warn": "Warning: applying a template OVERWRITES the current text of this document. Click Save afterwards to keep the change, or 'Undo changes' to revert.",
  "pol.template.empty": "No templates are available yet. Canonical policy templates are authored by the super-tenant (owner/counsel); that library is currently empty.",
  "pol.template.apply": "Apply (overwrites)",
  "pol.template.applied": "Template applied — not saved yet",
  // Controller · Verantwoording (COMPUTED accountability dashboard) — PLAN-W27 C; FRONTEND-BLUEPRINT §3.4
  "acc.title": "Accountability",
  "acc.sub": "Computed accountability overview — valid evidence per enabled activity (GDPR Art. 5(2))",
  "acc.error": "Failed to load the dashboard:",
  "acc.asOf": "Reference date",
  "acc.filter.beginMonth": "Begin month",
  "acc.filter.beginYear": "Begin year",
  "acc.filter.endMonth": "End month",
  "acc.filter.endYear": "End year",
  "acc.filter.hoofddoel": "Main goal",
  "acc.filter.activity": "Activity",
  "acc.filter.all": "All",
  "acc.filter.apply": "Apply",
  "acc.kpi.pct": "Accountability",
  "acc.kpi.pct.hint": "share of activities in order",
  "acc.kpi.inOrde": "In order",
  "acc.kpi.inOrde.hint": "activities with valid evidence / enabled",
  "acc.kpi.nonacc": "Non-accountability",
  "acc.kpi.nonacc.hint": "gaps in your accountability documentation",
  "acc.kpi.missing": "Missing evidence",
  "acc.kpi.missing.hint": "evidence requirements without a valid document",
  "acc.sections.title": "Accountability per section",
  "acc.sections.sub": "Share of activities in order per goal section.",
  "acc.sections.empty": "No sections in the selection.",
  "acc.section": "Section",
  "acc.missing.title": "Missing evidence",
  "acc.missing.sub": "Activities without valid evidence on the reference date.",
  "acc.missing.empty": "No missing evidence. Everything is in order.",
  "acc.missing.noContainer": "No evidence container linked",
  "acc.col.code": "Code",
  "acc.col.activity": "Activity",
  "acc.col.container": "Evidence",
  "acc.empty.title": "No activities enabled yet",
  "acc.empty.body": "Accountability is computed over the regime's enabled activities. Enable activities to get started.",
  "acc.empty.cta": "Configure regime",
  "acc.note.onDemand": "Point-in-time snapshot at the reference date — the monthly trend chart (non-accountability over time) follows.",
  // Rapporten — report catalog (FRONTEND-BLUEPRINT §3.6)
  "page.reports": "Reports",
  "page.reports.sub": "pre-defined parameterised reports, with export",
  "rep.request": "Request report",
  "rep.requesting": "Working…",
  "rep.soon": "coming soon",
  "rep.format": "Format",
  "rep.status": "Status",
  "rep.period": "Period",
  "rep.periodBegin": "Begin",
  "rep.periodEnd": "End",
  "rep.ticketType": "Ticket type",
  "rep.complexity": "Complexity",
  "rep.noParams": "No parameters.",
  "rep.failed": "Request failed:",
  "rep.status.all": "All", "rep.status.new": "New", "rep.status.active": "Active", "rep.status.inactive": "Inactive",
  "rep.cx.all": "All", "rep.cx.yes": "Yes", "rep.cx.no": "No",
  "rep.1.title": "Processing complexity",
  "rep.1.desc": "Ranking of processing activities by complexity.",
  "rep.2.title": "Activity planning",
  "rep.2.desc": "Organisation-wide activity planning overview.",
  "rep.3.title": "My activity planning",
  "rep.3.desc": "Activity planning for you only.",
  "rep.4.title": "Processing register",
  "rep.4.desc": "Processing register (Art. 30) with general info, purposes, processors and recipients.",
  "rep.5.title": "Filing-systems register",
  "rep.5.desc": "Filing-systems register with linked processings, location and purpose.",
  "rep.5.note": "Exports the filing-systems register as CSV.",
  "rep.6.title": "GDPR accountability status",
  "rep.6.desc": "GDPR accountability status: realisation percentages at three levels.",
  "rep.6.note": "Computed from the Accountability view (enabled activities × valid evidence). The period is a legacy parameter; the current calculation is as-of-today.",
  "rep.7.title": "Ticket report",
  "rep.7.desc": "Ticket overview by period and ticket type.",
  "rep.8.title": "Privacy-team report",
  "rep.8.desc": "Privacy-team overview.",
  "rep.9.title": "Processors report",
  "rep.9.desc": "Processors overview.",
  "rep.10.title": "Privacy-statement template",
  "rep.10.desc": "Privacy statement (Art. 13/14) based on selected processings.",
  "rep.tt.inzage": "Access request (Art. 15)",
  "rep.tt.beperking": "Restriction request (Art. 18)",
  "rep.tt.wissing": "Erasure request (Art. 17)",
  "rep.tt.portabiliteit": "Data portability request (Art. 20)",
  "rep.tt.intrekking": "Withdrawal of parental consent (Art. 7/8)",
  "rep.tt.datalek": "Data breach notification (Art. 33/34)",
  "rep.tt.klachten": "Complaints and objections (Art. 21/77)",
  "rep.tt.rectificatie": "Rectification request (Art. 16)",
  // Tickets persona — round-3 list + detail (PLAN-W27 D / FRONTEND-BLUEPRINT §3.5)
  "tk.sub": "DSR and data-breach work queue with 5-axis prioritisation",
  "tk.empty": "No tickets yet.",
  "tk.noMatches": "No tickets match these filters.",
  "tk.col.title": "Subject",
  "tk.col.status": "Status",
  "tk.col.priority": "Priority",
  "tk.col.kind": "Type",
  "tk.col.created": "Created",
  "tk.col.due": "Due",
  "tk.filter.status": "Filter by status",
  "tk.filter.kind": "Filter by ticket type",
  "tk.filter.allStatuses": "All statuses",
  "tk.filter.allKinds": "All types",
  "tk.kindsEmpty": "Ticket-kind library still empty (legal review).",
  "tk.createBlocked": "Creating tickets needs the canonical ticket-kind library, which is pending content seeding (legal review). Existing tickets can be opened, edited (incl. 5-axis priority) and deactivated.",
  "tk.new": "New ticket",
  "tk.detail.back": "Tickets",
  "tk.detail.title": "Ticket",
  "tk.detail.loadFailed": "Failed to load:",
  "tk.detail.notFound": "Ticket not found.",
  "tk.detail.edit": "Edit ticket",
  "tk.detail.workflow": "Status & workflow",
  "tk.detail.setStatus": "Set status to",
  "tk.detail.workflowNote": "Generic ticket lifecycle. The kind-specific workflow (state machine per ticket kind) is counsel-gated content and not yet available.",
  "tk.detail.priority": "Priority (5 axes)",
  "tk.detail.composite": "Composite score",
  "tk.detail.compositeHint": "The composite score is recomputed automatically whenever an axis changes.",
  "tk.detail.fields": "Details",
  "tk.detail.noHistory": "No change history yet.",
  "tk.axis.axisRegulatorySeverity": "Regulatory severity",
  "tk.axis.axisOperationalImpact": "Operational impact",
  "tk.axis.axisImplementationEffort": "Implementation effort",
  "tk.axis.axisDependencyDepth": "Dependency depth",
  "tk.axis.axisDeadlineProximity": "Deadline proximity",
  "tk.axis.unset": "not set",
  "tk.scale.very_low": "Very low",
  "tk.scale.low": "Low",
  "tk.scale.medium": "Medium",
  "tk.scale.high": "High",
  "tk.scale.very_high": "Very high",
  "tk.lc.draft": "Draft",
  "tk.lc.open": "Open",
  "tk.lc.in_progress": "In progress",
  "tk.lc.blocked": "Blocked",
  "tk.lc.in_review": "In review",
  "tk.lc.done": "Done",
  "tk.lc.cancelled": "Cancelled",
  "tk.lc.deferred": "Deferred",
  "tk.field.kind": "Ticket kind (canonical)",
  "tk.field.type": "Ticket type (migration)",
  "tk.field.body": "Description",
  "tk.field.verwerking": "Linked processing activity",
  "tk.field.source": "Source",
  "tk.field.assigned": "Assigned to",
  "tk.field.language": "Language",
  "tk.field.created": "Created",
  "tk.field.due": "Due",
  "tk.field.resolved": "Resolved",
  "tk.field.currentState": "Kind-specific state",
};
const CATALOG: Record<Lang, Catalog> = { nl: NL, en: EN };

interface I18nValue { lang: Lang; t: (key: string, ...args: (string | number)[]) => string; setLang: (l: Lang) => void }
const I18nCtx = createContext<I18nValue>({ lang: "nl", t: (k) => k, setLang: () => {} });

/** Replace {0}, {1}, … positional placeholders. */
function interpolate(s: string, args: (string | number)[]): string {
  return args.length ? s.replace(/\{(\d+)\}/g, (m, i) => (args[Number(i)] != null ? String(args[Number(i)]) : m)) : s;
}

function initialLang(defaultLanguage?: string): Lang {
  try {
    const stored = localStorage.getItem("sympllex_lang");
    if (stored === "en" || stored === "nl") return stored;
  } catch { /* no localStorage (SSR) */ }
  return defaultLanguage?.toLowerCase().startsWith("en") ? "en" : "nl"; // NL default
}

export function I18nProvider({ children, defaultLanguage }: { children: ReactNode; defaultLanguage?: string }) {
  const [lang, setLangState] = useState<Lang>(() => initialLang(defaultLanguage));
  const setLang = useCallback((l: Lang) => {
    try { localStorage.setItem("sympllex_lang", l); } catch { /* ignore */ }
    setLangState(l);
  }, []);
  useEffect(() => { try { document.documentElement.lang = lang; } catch { /* ignore */ } }, [lang]);
  const t = useCallback((key: string, ...args: (string | number)[]) => interpolate(CATALOG[lang][key] ?? CATALOG.nl[key] ?? key, args), [lang]);
  return <I18nCtx.Provider value={{ lang, t, setLang }}>{children}</I18nCtx.Provider>;
}

export function useI18n(): I18nValue { return useContext(I18nCtx); }
